import VerifiedIcon from "@mui/icons-material/Verified";
import { Avatar } from "@mui/material";
import React, { useMemo } from "react";
import { useAppSelector } from "../../hooks";
import useBasicProfile from "../../hooks/useBasicProfile";
import { useRouter } from "../../hooks/useRouter";
import useSocialConnection from "../../hooks/useSocialConnection";
import { ROUTES } from "../../routes";
import { TMessage } from "../../types/types";
import {
  isAssetUrl,
  isListUrl,
  truncateUsername,
} from "../../utils/format.utils";
import ChatListItemLoader from "./ChatListItemLoader";
import "./index.css";
import Blockies from "react-blockies";

type Prop = {
  message: TMessage;
  toAddress?: string;
};
const ChatListItem = ({ message, toAddress }: Prop) => {
  const { currentAddress } = useAppSelector((state) => state.auth);
  const { push } = useRouter();
  const targetAddress = useMemo(() => {
    if (message.fromAddress === currentAddress) return message.toAddress;
    return message.fromAddress;
  }, [currentAddress, message]);
  const { contacts } = useSocialConnection();

  const nickName = useMemo(() => {
    if (contacts.length === 0) return "";
    const targetContact = contacts.find(
      (contact) => contact.target === targetAddress
    );
    if (!targetContact?.alias) return "";
    return targetContact.alias;
  }, [contacts.length, targetAddress]);
  const { basicProfile } = useBasicProfile(targetAddress);
  const { secondFontColor } = useAppSelector((state) => state.layout);

  const msgTime = () => {
    const today = new Date();
    const messageTime = new Date(message.createdAt);
    if (
      today.toDateString().slice(-4) === messageTime.toDateString().slice(-4)
    ) {
      if (today.toDateString() === messageTime.toDateString()) {
        // For the today messages, we only display the time
        return messageTime.toTimeString().slice(0, 5);
      }
      // For the messages sent in the current year, we only display the date without year info
      return messageTime.toLocaleString("en-GB").slice(0, 5);
    }
    return messageTime.toLocaleString("en-GB").slice(0, 10);
  };

  const getText = () => {
    if (isAssetUrl(message.plaintextMessage)) return "[NFT]";
    if (isListUrl(message.plaintextMessage)) return "[LIST]";
    return message.plaintextMessage;
  };

  const getAddress = () => {
    if (!basicProfile) return;
    if (basicProfile.isENSVerified) return basicProfile.name;
    return targetAddress;
  };

  if (!basicProfile) {
    return <ChatListItemLoader />;
  }

  return (
    <li className={`${toAddress === targetAddress && "active"}`}>
      <div
        className="dm-item"
        onClick={(e) => {
          const target = e.target as Element;
          const isChild = (e.currentTarget as Element)?.contains(target);
          push({
            pathname: ROUTES.DM_PAGE + `/${targetAddress}`,
            search: `?alias=${nickName ? nickName : basicProfile.name}`,
          });
        }}
      >
        {basicProfile.avatar_url ? (
          <Avatar
            src={basicProfile.avatar_url}
            style={{
              width: "40px",
              height: "40px",
            }}
          />
        ) : (
          <Blockies
            seed={targetAddress}
            size={10}
            scale={4}
            color="#ddd"
            className="identicon"
          />
        )}
        <div className="chat-item-description-container">
          <div className="chat-item-user-name-block">
            <div className="chat-item-user-name">
              {truncateUsername(
                nickName ? nickName : basicProfile.name ?? targetAddress
              )}{" "}
              ({truncateUsername(getAddress())})
            </div>
            {!!basicProfile.isENSVerified && (
              <VerifiedIcon
                color="primary"
                fontSize="small"
                style={{
                  verticalAlign: "middle",
                  margin: "-1px 0 0 4px",
                }}
              />
            )}
          </div>
          <div
            className="chat-item-user-name"
            style={{ color: secondFontColor }}
          >
            {getText()}
          </div>
        </div>
        <div className="chat-item-time" style={{ color: secondFontColor }}>
          {msgTime()}
        </div>
      </div>
    </li>
  );
};
export default ChatListItem;
